import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    Chip,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import { Sparkles, AlertCircle, Calendar } from 'lucide-react';

export const AIFormattedSummary = ({ rawText, loading = false, summaryType = 'brief', daysBack = 7, onTypeChange, onDaysChange }) => {
    const [thinkingPhrase, setThinkingPhrase] = React.useState(0);

    const thinkingPhrases = [
        "Analyzing your health data...",
        "Processing vital signs...",
        "Identifying patterns...",
        "Generating insights...",
        "Compiling summary...",
        "Almost there...",
        "Finalizing recommendations..."
    ];

    // Rotate thinking phrases while loading
    React.useEffect(() => {
        if (!loading) return;

        const interval = setInterval(() => {
            setThinkingPhrase((prev) => (prev + 1) % thinkingPhrases.length);
        }, 2000); // Change phrase every 2 seconds

        return () => clearInterval(interval);
    }, [loading, thinkingPhrases.length]);


    // Parse inline markdown (bold text) and clean up ** markers
    const parseInlineMarkdown = (text) => {
        const parts = [];
        let lastIndex = 0;
        const boldRegex = /\*\*(.+?)\*\*/g;
        let match;

        while ((match = boldRegex.exec(text)) !== null) {
            // Add text before bold
            if (match.index > lastIndex) {
                parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
            }
            // Add bold text
            parts.push({ type: 'bold', content: match[1] });
            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push({ type: 'text', content: text.substring(lastIndex) });
        }

        return parts.length > 0 ? parts : [{ type: 'text', content: text }];
    };

    // Clean text by removing ** markers
    const cleanText = (text) => {
        return text.replace(/\*\*/g, '');
    };

    // Parse markdown-style text into structured content
    const parseMarkdownText = (text) => {
        if (!text) return [];

        const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
        const sections = [];
        let currentSection = null;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Handle disclaimer
            if (line.startsWith('⚠️') || line.toLowerCase().includes('disclaimer')) {
                sections.push({
                    type: 'disclaimer',
                    content: cleanText(line) // Remove ** markers from disclaimer
                });
                continue;
            }

            // Check for section heading: "- **Heading**:" or "**Heading**:"
            const headingMatch = line.match(/^-?\s*\*\*(.+?)\*\*:?(.*)$/);
            if (headingMatch) {
                // Save previous section if exists
                if (currentSection) {
                    sections.push(currentSection);
                }
                // Start new section
                currentSection = {
                    type: 'section',
                    heading: headingMatch[1].trim(),
                    items: headingMatch[2].trim() ? [parseInlineMarkdown(headingMatch[2].trim())] : []
                };
                continue;
            }

            // Check for bullet points: "- text" or "• text"
            const bulletMatch = line.match(/^[-•]\s+(.+)$/);
            if (bulletMatch) {
                if (!currentSection) {
                    currentSection = {
                        type: 'section',
                        heading: 'Summary',
                        items: []
                    };
                }
                currentSection.items.push(parseInlineMarkdown(bulletMatch[1]));
                continue;
            }

            // Regular text line
            if (line) {
                if (!currentSection) {
                    currentSection = {
                        type: 'section',
                        heading: 'Summary',
                        items: []
                    };
                }
                currentSection.items.push(parseInlineMarkdown(line));
            }
        }

        // Add last section
        if (currentSection) {
            sections.push(currentSection);
        }

        return sections;
    };

    // Render inline markdown parts
    const renderInlineMarkdown = (parts) => {
        return parts.map((part, idx) => {
            if (part.type === 'bold') {
                return (
                    <Typography
                        key={idx}
                        component="span"
                        sx={{ fontWeight: 700, color: 'primary.main' }}
                    >
                        {part.content}
                    </Typography>
                );
            }
            return <span key={idx}>{part.content}</span>;
        });
    };

    const sections = parseMarkdownText(rawText);

    // Show loading state with animated thinking phrases
    if (loading) {
        return (
            <Card
                elevation={3}
                sx={{
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 3
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            py: 2.5,
                            px: 3,
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderBottom: '3px solid',
                            borderColor: 'primary.main'
                        }}
                    >
                        <Sparkles size={24} className="text-purple-600 animate-pulse" />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '0.5px'
                            }}
                        >
                            AI Generating Summary
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 6,
                            bgcolor: 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': {
                                        opacity: 1,
                                        transform: 'scale(1)'
                                    },
                                    '50%': {
                                        opacity: 0.7,
                                        transform: 'scale(1.05)'
                                    }
                                }
                            }}
                        >
                            <Sparkles size={40} className="text-white" />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                textAlign: 'center',
                                animation: 'fadeIn 0.5s ease-in-out',
                                '@keyframes fadeIn': {
                                    from: { opacity: 0 },
                                    to: { opacity: 1 }
                                }
                            }}
                        >
                            {thinkingPhrases[thinkingPhrase]}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[0, 1, 2].map((i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                        animation: `bounce 1.4s infinite ease-in-out both`,
                                        animationDelay: `${i * 0.16}s`,
                                        '@keyframes bounce': {
                                            '0%, 80%, 100%': {
                                                transform: 'scale(0)',
                                                opacity: 0.5
                                            },
                                            '40%': {
                                                transform: 'scale(1)',
                                                opacity: 1
                                            }
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            elevation={3}
            sx={{
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 3
            }}
        >
            <CardContent sx={{ p: 0 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        py: 2.5,
                        px: { xs: 2, sm: 3 },
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderBottom: '3px solid',
                        borderColor: 'primary.main',
                        gap: 2
                    }}
                >
                    {/* Left: Title and Badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Sparkles size={24} className="text-purple-600" />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '0.5px',
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            }}
                        >
                            AI Summary
                        </Typography>
                        <Chip
                            label={summaryType === 'brief' ? 'Brief' : 'Comprehensive'}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                bgcolor: summaryType === 'brief' ? '#10b981' : '#8b5cf6',
                                color: 'white',
                                height: '24px',
                                textTransform: 'capitalize'
                            }}
                        />
                    </Box>

                    {/* Right: Controls */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1.5, sm: 2 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        {/* Toggle Button */}
                        <ToggleButtonGroup
                            value={summaryType}
                            exclusive
                            onChange={(e, newType) => {
                                if (newType !== null && onTypeChange) {
                                    onTypeChange(newType)
                                }
                            }}
                            size="small"
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                '& .MuiToggleButton-root': {
                                    px: { xs: 1.5, sm: 2 },
                                    py: 0.5,
                                    fontSize: { xs: '0.75rem', sm: '0.813rem' },
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    border: '1.5px solid',
                                    borderColor: 'primary.main',
                                    flex: { xs: 1, sm: 'initial' },
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'primary.dark'
                                        }
                                    },
                                    '&:not(.Mui-selected)': {
                                        color: 'primary.main',
                                        bgcolor: 'white',
                                        '&:hover': {
                                            bgcolor: 'primary.lighter'
                                        }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="brief">Brief</ToggleButton>
                            <ToggleButton value="comprehensive">Comprehensive</ToggleButton>
                        </ToggleButtonGroup>

                        {/* Days Selector */}
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 }, width: { xs: '100%', sm: 'auto' } }}>
                            <Select
                                value={daysBack}
                                onChange={(e) => {
                                    if (onDaysChange) {
                                        onDaysChange(e.target.value)
                                    }
                                }}
                                startAdornment={
                                    <Calendar size={16} className="text-gray-500 mr-1" />
                                }
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.813rem' },
                                    fontWeight: 600,
                                    bgcolor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                        borderWidth: '1.5px'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.dark'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                <MenuItem value={7}>Last 7 days</MenuItem>
                                <MenuItem value={15}>Last 15 days</MenuItem>
                                <MenuItem value={30}>Last 30 days</MenuItem>
                                <MenuItem value={45}>Last 45 days</MenuItem>
                                <MenuItem value={60}>Last 60 days</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, bgcolor: 'background.paper' }}>
                    {sections.length === 0 ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No summary available
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {sections.map((section, idx) => {
                                if (section.type === 'disclaimer') {
                                    return (
                                        <Paper
                                            key={idx}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                bgcolor: 'warning.lighter',
                                                border: '1px solid',
                                                borderColor: 'warning.light',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1.5
                                            }}
                                        >
                                            <AlertCircle size={20} className="text-orange-500 mt-0.5 flex-shrink-0" />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.875rem',
                                                    lineHeight: 1.6,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {section.content}
                                            </Typography>
                                        </Paper>
                                    );
                                }

                                return (
                                    <Box key={idx}>
                                        {/* Section Heading */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1.5
                                            }}
                                        >
                                            <Chip
                                                label={section.heading}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    bgcolor: 'primary.main',
                                                    color: 'white',
                                                    px: 1,
                                                    '& .MuiChip-label': {
                                                        px: 1.5
                                                    }
                                                }}
                                            />
                                            <Divider sx={{ flexGrow: 1 }} />
                                        </Box>

                                        {/* Section Items */}
                                        {section.items && section.items.length > 0 && (
                                            <Box
                                                component="ul"
                                                sx={{
                                                    listStyle: 'none',
                                                    pl: 0,
                                                    m: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5
                                                }}
                                            >
                                                {section.items.map((item, i) => (
                                                    <Box
                                                        key={i}
                                                        component="li"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 1.5,
                                                            pl: 2
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: '50%',
                                                                bgcolor: 'primary.main',
                                                                mt: 1,
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.primary',
                                                                fontSize: '0.9375rem',
                                                                lineHeight: 1.7,
                                                                flex: 1
                                                            }}
                                                        >
                                                            {renderInlineMarkdown(item)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
